<script>
  import { format } from 'svelte-i18n';
  import { Button, Input, Modal } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { DomainsService } from '@client/services/domains';
  import { TagBlocksService } from '@client/services/tag-blocks';

  import { userStore } from '@client/stores/user';

  import { sanitizeInputValue } from '@client/utils/sanitize';

  import { debounce } from '@common/utils/debounce';

  const { addNotification } = getNotificationsContext();

  export let show = false;
  export let domain = '';
  export let signature = {};
  export let rulename = '';
  export let domains = [];
  export let updating = false;

  let selectedDomains = [];
  let filterText = '';
  let searchingDomains = false;

  const onError = (text) => {
    addNotification({
      text,
      type: 'error',
      autohide: 2500,
      position: 'top-right'
    });
  };

  let createNewDomain = false;

  const searchDomains = debounce(async (query) => {
    if (!query || query.length < 2) {
      return;
    }

    searchingDomains = true;

    try {
      const response = await DomainsService.getAll({
        query: query,
        screen: $userStore.isStaff ? 'staff' : '',
        perPage: 100
      });

      const data = Array.isArray(response?.data) ? response.data : [];
      const searchResults = data.map(({ domainname }) => ({ value: domainname, label: domainname }));

      // Merge with existing domains, avoiding duplicates
      const existingValues = new Set(domains.map((d) => d.value));
      const newDomains = searchResults.filter((d) => !existingValues.has(d.value));
      domains = [...domains, ...newDomains];
    } catch (err) {
      console.error('Domain search error:', err);
    }

    searchingDomains = false;
  }, 300);

  const onFilter = (e) => {
    const filteredResults = e.detail;
    createNewDomain = filteredResults.length === 0 && filterText.length;

    // Trigger server-side search when filtering
    if (filterText && filterText.length >= 2) {
      searchDomains(filterText);
    }
  };

  const onKeyUp = (e) => {
    if (e.key === 'Enter') {
      if (createNewDomain) {
        selectedDomains = [
          ...selectedDomains,
          {
            value: filterText,
            label: filterText
          }
        ];

        filterText = '';
      }
    }
  };

  const resetModal = () => {
    show = false;
    signature = null;
    rulename = '';
    selectedDomains = [];
    filterText = '';
    searchingDomains = false;
    createNewDomain = false;
  };

  const onInputKeyUp = () => {
    rulename = sanitizeInputValue(rulename);
  };

  const onClone = async () => {
    const destdomains = selectedDomains.map(({ value }) => value).join(' ');
    const sourcetagblock = signature.rulename;
    const targettagblock = rulename;

    updating = true;

    const response = await TagBlocksService.clone({
      cloneOptions: {},
      destdomains,
      sourcedomain: domain,
      sourcetagblock,
      targettagblock
    });

    updating = false;

    if (response.status >= 400) {
      resetModal();
      return onError(
        $format('label.FAILURE.DOMAIN_CLONE_PUSHER', { values: { name: sourcetagblock, destination: destdomains } })
      );
    }

    addNotification({
      text: $format('label.SUCCESS.CLONE', { values: { type: sourcetagblock } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    resetModal();
  };

  $: show === false && resetModal();
</script>

<Modal bind:isOpen={show} size="sm">
  <div class="clone-to-domain-modal modal--content">
    <button class="close" on:click={() => (show = !show)}>
      <Icon name="x" />
    </button>
    <div>
      <h1>
        {$format('label.CLONE')} <b>{signature.rulename}</b>
      </h1>
      <p>{$format('label.SELECT_DOMAINS_TO_CLONE')}</p>
      <div class="domain-selector" on:keyup={onKeyUp} role="button" tabindex="0">
        <Select
          on:filter={onFilter}
          bind:filterText
          bind:value={selectedDomains}
          bind:items={domains}
          multiple
          searchable
          placeholder={$format('label.PLACEHOLDER.SELECT_DOMAIN')}
          showChevron
        >
          <slot name="empty" slot="empty">
            <div class="empty">
              {#if searchingDomains}
                <Spinner show inline />
              {:else if createNewDomain}
                {@html $format('label.PRESS_ENTER_TO_ADD_DOMAIN', { values: { item: filterText } })}
              {:else}
                {$format('label.NO_DOMAINS_MATCH')}
              {/if}
            </div>
          </slot>
        </Select>
      </div>
      <p>
        {@html $format('label.ENTER_NEW_NAME_FOR_CLONED_DOMAIN')}
      </p>
      <Input
        type="text"
        bind:value={rulename}
        on:keyup={onInputKeyUp}
        placeholder={$format('label.PLACEHOLDER.CLONE_SIGNATURE_RULENAME')}
      />
    </div>
    <div class="footer-actions">
      <Button color="primary" disabled={!selectedDomains?.length} on:click={onClone}
        >{$format('label.CLONE_SIGNATURE')}</Button
      >
    </div>
  </div>
</Modal>

<style lang="scss" src="./clone.scss"></style>
